<template>
  <div
    id="filter-keywords"
    v-click-outside="closeFiltersDropdown"
    class="filter-keywords__wrapper dropdown_container"
    data-cy="filter-keywords-wrap"
  >
    <kw-button
      button-type="secondary"
      prepend-icon-class="kw kw-filter"
      data-cy="dashboard-filter-button"
      @click="handleFilterIconClick"
    />
    <custom-dropdown-body ref="filtersDropdown" position="bottom-right">
      <div class="v-context" data-cy="dashboard-filter-menu">
        <div class="filter" data-cy="show-keywords-filter">
          <h4 class="filter__title">
            {{ $t('show') }}
          </h4>
          <v-select
            data-cy="show-filter-field"
            :options="keywordsShowOptions"
            :value="filters.showFilterValue"
            @change="(val) => onFilterChange(val, 'showFilterValue')"
          />
          <label v-if="false" class="toggler-label">
            <toggler :toggler="getIsHideURLKeywords || false" @toggler_click="toggleIsHideUrlKeywords" />
            {{ $t('hide-url-keywords') }}
          </label>
        </div>
        <div class="filter" data-cy="search-engine-filter">
          <h4 class="filter__title">
            {{ $t('search-engine') }}
          </h4>
          <v-select
            data-cy="search-engine-field"
            :options="keywordsEnginesOptions"
            :value="filters.enginesValue"
            @change="(val) => onFilterChange(val, 'enginesValue')"
          />
        </div>
        <div class="filter" data-cy="report-date-filter">
          <h4 class="filter__title">
            {{ $t('change-report-date') }}
          </h4>
          <datepicker
            class="datepicker_right-popup"
            :clear-button="true"
            :clear-date="() => onFilterChange('', 'keywordsDate')"
            format="dd MMMM yyyy"
            :placeholder="filters.keywordsDate || getFormattedDate(new Date())"
            :value.sync="filters.keywordsDate"
            @input="(val) => onFilterChange(val, 'keywordsDate')"
          />
        </div>
        <div v-if="!getActiveTag.id" class="filter filter_tags" data-cy="tags-filter">
          <h4 class="filter__title">
            {{ $t('tags-uppercased') }}
          </h4>
          <items-filter
            data-cy="tags-filter-field"
            :button-text="tagsButtonText"
            :check-callback="(val) => onFilterChange(val, 'activeTag')"
            :items="tagsOptions"
            :menu-id="'tags-filter-menu'"
            :selected-item="filters.activeTag"
            :trigger-id="'tags-filter-trigger'"
            :button-disabled="!!getShareViewHashData"
            :clear-filter="clearFilter"
          >
            <template slot="label" slot-scope="{ item }">
              <span class="tag-label">{{ item }}</span>
            </template>
          </items-filter>
        </div>
        <div v-if="false" class="min-max">
          <input
            v-model="getGrankRange.min"
            type="text"
            placeholder="Min"
            class="text-input"
            @input="debouncedHandler(updateAndFetchKeywords)"
          />
          to
          <input
            v-model="getGrankRange.max"
            placeholder="Max"
            type="text"
            class="text-input"
            @input="debouncedHandler(updateAndFetchKeywords)"
          />
        </div>
        <div class="filter">
          <kw-button :button-text="$t('apply')" button-type="primary" data-cy="apply-filters-button" @click="save" />
        </div>
      </div>
    </custom-dropdown-body>
    <global-events @keyup.enter="enterHandler" @keyup.esc="escHandler" />
  </div>
</template>

<script>
import { Events, EventBus } from '@/events';
import { keywordsShowOptions, keywordsEnginesOptions } from './configs';
import { mapGetters, mapActions } from 'vuex';
import { switchDateFormat } from '@/helpers/format-date-service';
import { TYPING_DEBOUNCE } from '@/constants';
import ItemsFilter from 'components/ui-elements/ItemsFilter';
import vSelect from 'vue-strap/src/Select';
import VueStrapDataPicker from 'components/extended/VueStrapDataPicker';

export default {
  name: 'FilterKeywords',
  components: {
    datepicker: VueStrapDataPicker,
    'items-filter': ItemsFilter,
    'v-select': vSelect,
  },
  data() {
    return {
      debouncedHandler: _.debounce((fn) => fn(), TYPING_DEBOUNCE),
      keywordsEnginesOptions,
      keywordsShowOptions,
      switchDateFormat,
      filters: {
        activeTag: {},
        enginesValue: '',
        keywordsDate: '',
        showFilterValue: 'all',
      },
    };
  },
  computed: {
    ...mapGetters([
      'getActiveTag',
      'getGrankRange',
      'getFormattedDate',
      'getKeywordFilters',
      'getCurrentProject',
      'getEditableProject',
      'getProjectProperty',
      'getShareViewHashData',
      'getIsHideURLKeywords',
    ]),
    tagsOptions() {
      return this.getCurrentProject ? this.getCurrentProject.tags : [];
    },
    tagsButtonText() {
      if (this.getShareViewHashData) {
        const activeTag = this.tagsOptions.find((item) => item.id === this.getShareViewHashData.filters.tagId);
        return activeTag ? activeTag.name : 'None';
      }

      let { activeTag } = this.filters;
      return activeTag.id ? activeTag.name : 'None';
    },
  },
  watch: {
    getActiveTag() {
      this.filters = {
        ...this.filters,
        activeTag: {},
      };
      this.updateFilters({
        params: {
          ...this.getKeywordFilters,
          activeTag: {},
        },
        skipFetch: true,
      });
    },
    getCurrentProject() {
      this.filters = {
        ...this.filters,
        enginesValue: '',
        keywordsDate: '',
        showFilterValue: 'all',
        activeTag: {},
      };
    },
  },
  mounted() {
    const { enginesValue, keywordsDate, showFilterValue, activeTag } = this.getKeywordFilters;
    this.filters = {
      ...this.filters,
      enginesValue,
      keywordsDate,
      showFilterValue,
      activeTag,
    };
  },
  destroyed() {
    this.updateFilters({
      params: {
        ...this.getKeywordFilters,
        enginesValue: '',
        keywordsDate: '',
        showFilterValue: 'all',
        activeTag: {},
      },
      skipFetch: true,
    });
  },
  methods: {
    ...mapActions([
      'fetchMetrics',
      'fetchKeywords',
      'updateFilters',
      'setGrankRange',
      'toggleIsHideUrlKeywords',
      'fetchViewkeyMetricsAndChart',
      'fetchKeywordsForShareViewKeys',
    ]),
    enterHandler() {
      if (!this.$refs.filtersDropdown.getIsDropdownOpened || this.getEditableProject) {
        return;
      }

      this.save();
    },
    escHandler() {
      this.$refs.filtersDropdown.closeDropdown();
    },
    onFilterChange(val, type) {
      this.filters = {
        ...this.filters,
        [type]: val,
      };
    },
    save() {
      const { enginesValue, keywordsDate, showFilterValue, activeTag } = this.filters;
      const filters = {
        params: {
          keywordsDate: keywordsDate ? switchDateFormat(new Date(keywordsDate), 'Y-m-d') : '',
          enginesValue,
          showFilterValue,
          activeTag,
        },
      };
      this.updateFilters(filters);
      EventBus.emit(Events.USED_TABLE_FILTER, filters);
      this.closeFiltersDropdown();
    },
    async updateAndFetchKeywords() {
      const { max, min } = this.getGrankRange;

      if (
        (!min && max > 100) ||
        (!min && max < 1) ||
        (!max && min > 100) ||
        (!max && min < 1) ||
        (max && min && (max > 100 || max < 1 || min < 1 || min > 100))
      ) {
        this.setGrankRange({ max: '', min: '' });
        return this.$toastr.e(this.$t('grank-range'));
      }

      this.setGrankRange(this.getGrankRange);
      await this.fetchData();
    },
    async fetchData() {
      if (
        this.$route.name === 'shareViewkey' ||
        this.$route.name === 'shareViewkeyWithHash' ||
        this.$route.name === 'shareKeywordsTable' ||
        this.$route.name === 'shareKeywordsTableWithHash'
      ) {
        const data = {
          shortkey: this.getProjectProperty('shortkey'),
          id: this.getProjectProperty('auth'),
          password: this.getProjectProperty('password'),
        };

        this.fetchViewkeyMetricsAndChart();
        this.fetchKeywordsForShareViewKeys(data);
      } else {
        this.fetchMetrics();
        this.fetchKeywords({ id: this.getCurrentProject.id });
      }
    },
    handleFilterIconClick() {
      this.$refs.filtersDropdown.toggleDropdownOpen();
    },
    closeFiltersDropdown() {
      if (this.$refs.filtersDropdown?.getIsDropdownOpened) {
        this.$refs.filtersDropdown.closeDropdown();
      }
    },
    clearFilter() {
      this.filters.activeTag = {};
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-keywords__wrapper {
  position: relative;

  button {
    &.kw-btn-secondary {
      padding: 8px 8px;

      ::v-deep .kw-filter {
        font-size: 18px;
        font-weight: 700;
      }
    }
  }

  .v-context {
    max-height: unset;
    overflow-y: unset;
    width: 248px;
    position: relative;
  }

  label {
    position: relative;
    padding-left: 45px;
    font-style: normal;
    font-weight: normal;
    line-height: 20px;
    font-size: 14px;
    color: kw-color(kw-black, 1);
  }

  .items-filter__item:hover {
    .only-one-filter {
      visibility: visible;
    }
  }
}

.min-max {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding-bottom: 16px;

  input {
    width: 92px;
  }
}

.tag-checkbox {
  @extend .custom-scroll;
  height: 150px;
  overflow-y: auto;

  p {
    position: relative;
    display: flex;
    margin-top: 0 !important;
    padding-left: 26px;
    margin-bottom: 9px;
  }
}

.toggler-label {
  margin-top: 16px;
}

.toggler.toggler_abled.toggler_inactive {
  position: absolute;
  left: 0;
}

.toggler.toggler_abled.toggler_active {
  left: 0;
}

::v-deep .top-bar__button-filter {
  i {
    font-size: 20px;
  }
}

.filter {
  padding: 16px;

  &:not(:last-child) {
    border-bottom: 1px solid #dee5ec;
  }

  &:last-child {
    padding-bottom: 10px;

    ::v-deep .kw-btn-primary {
      width: 100%;
    }
  }
}

.filter_tags {
  ::v-deep .kw-button {
    border: 1px solid #dee5ec;
    border-radius: 4px;
    box-shadow: none;
    color: kw-color(kw-black, 1) !important;
    justify-content: space-between;
    margin-top: 12px;
    width: 100%;

    &.button-component_active {
      .kw-chevron-down {
        transform: rotate(180deg);
      }
    }

    .kw-chevron-down {
      transform: rotate(0deg);
    }
  }

  ::v-deep .items-filter__popup {
    width: 100%;
  }
}

.filter__title {
  color: #cccccc;
  font-size: 13px;
  font-weight: 500;
  line-height: 15px;
  margin: 0;
}

.btn-group {
  margin-top: 12px;
  width: 100%;
}

.datepicker_custom {
  border-radius: 4px;
  box-shadow: none;
  margin-top: 12px;
}

::v-deep .datepicker-popup_custom {
  min-width: 248px;
}

.tag-label {
  @import '~sass/cutting-name';
  border-radius: 4px;
  box-sizing: border-box;
  color: #265596;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 19px;
  max-width: 190px;
  padding: 0 3px !important;
  text-align: center;
  text-transform: lowercase;
}

::v-deep .icon-filter-filled {
  &:before {
    background: url('~img/icon-filter-filled.svg');
    display: block;
    background-repeat: no-repeat;
    content: '';
    width: 17px;
    height: 18px;
  }
}

.custom_dropdown_body {
  box-shadow: none !important;
}
</style>
